import { useEffect } from 'react'
import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'

export interface ChatBubbleData {
  id: string
  seatIndex: number
  message: string
  isEmoji: boolean
  timestamp: number
}

interface ChatBubbleProps {
  bubble: ChatBubbleData
  onExpire: (id: string) => void
}

export function ChatBubble({ bubble, onExpire }: ChatBubbleProps) {
  useEffect(() => {
    const timer = setTimeout(() => onExpire(bubble.id), bubble.isEmoji ? 3000 : 4000)
    return () => clearTimeout(timer)
  }, [bubble.id, bubble.isEmoji, onExpire])

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.5, y: 8 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.8, y: -4 }}
      transition={{ type: 'spring', damping: 20, stiffness: 300 }}
      className={cn(
        'absolute -top-10 left-1/2 -translate-x-1/2 z-40 pointer-events-none',
        'px-2 py-1 rounded-lg bg-black/70 backdrop-blur-sm border border-white/10',
        'whitespace-nowrap max-w-[120px]'
      )}
    >
      <span className={cn(
        bubble.isEmoji ? 'text-lg' : 'text-[10px] text-foreground/90 font-medium truncate block'
      )}>
        {bubble.message}
      </span>
      {/* Tail */}
      <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-black/70 rotate-45 border-r border-b border-white/10" />
    </motion.div>
  )
}

// Quick emoji presets for the action zone
export const QUICK_EMOJIS = ['😂', '👍', '👏', '🔥'] as const

interface EmojiBarProps {
  onSendEmoji: (emoji: string) => void
  onOpenChat: () => void
  isCompact: boolean
}

export function EmojiBar({ onSendEmoji, onOpenChat, isCompact }: EmojiBarProps) {
  return (
    <div className="flex items-center gap-1.5">
      {QUICK_EMOJIS.map((emoji) => (
        <motion.button
          key={emoji}
          whileTap={{ scale: 0.85 }}
          onClick={() => onSendEmoji(emoji)}
          className={cn(
            'rounded-lg bg-surface-elevated hover:bg-white/10 transition-colors flex items-center justify-center',
            isCompact ? 'w-8 h-8 text-base' : 'w-9 h-9 text-lg'
          )}
        >
          {emoji}
        </motion.button>
      ))}
      <motion.button
        whileTap={{ scale: 0.85 }}
        onClick={onOpenChat}
        className={cn(
          'rounded-lg bg-surface-elevated hover:bg-white/10 transition-colors flex items-center justify-center text-muted-foreground',
          isCompact ? 'w-8 h-8 text-[10px]' : 'w-9 h-9 text-xs'
        )}
      >
        💬
      </motion.button>
    </div>
  )
}
